import type { OutgoingSearchResults, PassioConcernCode } from "./models";

/**
 * Response shapes that differ from the published spec. Each normalizer accepts both the
 * documented shape and what the API actually sends, so it keeps working once the API is fixed.
 */

type Loose = Record<string, unknown>;
const isObject = (v: unknown): v is Loose => typeof v === "object" && v !== null && !Array.isArray(v);
const pick = (o: Loose, ...keys: string[]) => keys.map((k) => o[k]).find((v) => v !== undefined);

/** Spec: `PassioConcernCode[]`. API: an object keyed by Go field name with capitalized fields. */
export function normalizeConcernCodes(data: unknown): PassioConcernCode[] {
  const items = Array.isArray(data) ? data : isObject(data) ? Object.values(data) : [];
  return items
    .filter(isObject)
    .map((c) => ({
      code: pick(c, "code", "Code") as number | undefined,
      name: pick(c, "name", "Name") as string | undefined,
      description: pick(c, "description", "Description") as string | undefined,
      severity: pick(c, "severity", "Severity") as number | undefined,
    }))
    .sort((a, b) => (a.code ?? 0) - (b.code ?? 0));
}

/** Advanced search can leave a `null` entry in `results` when an item has no score. */
export function normalizeSearchResults(data: OutgoingSearchResults): OutgoingSearchResults {
  if (!isObject(data) || !Array.isArray(data.results)) return data;
  return { ...data, results: data.results.filter((r) => r != null) };
}
